import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Maximize2, Search, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { EYEBROW, FOCUS_RING } from '../constants';
import { PanelHeading } from '../controls';
import { FontPicker } from '../FontPicker';

// Same-origin request: azamein.com proxies /api to the backend, so no cross-site (CORS) step that a
// filter or proxy on the customer's computer can break. "charmap/<font>" and not "glyphs?family=":
// the Geder content filter blocks the latter and answers with its HTML block page.
const glyphsUrl = (family) => `/api/fonts/charmap/${encodeURIComponent(family)}`;
const RECENT_KEY = 'azamein.recentGlyphs';
const FAV_KEY = 'azamein.favoriteGlyphs';
const GROUP_ORDER = ['Alternates', 'Ornaments', 'Decorative (private)', 'Shapes & frames', 'Symbols', 'Hebrew', 'Letters & numbers', 'Punctuation', 'Braille', 'Other'];
const SIZES = [
  [30, 'S'],
  [42, 'M'],
  [60, 'L'],
  [84, 'XL'],
];

const readList = (key, max) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v.filter((g) => g && g.ch && g.family).slice(0, max) : [];
  } catch (e) {
    return [];
  }
};
const writeList = (key, list) => {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {
    // ignore
  }
};
const codeOf = (cp) => `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
const glyphCache = {}; // family -> response (session)

/** The glyph browser itself (used in the side panel and in the large window). */
const GlyphBrowser = ({ fonts, family, setFamily, onInsert, targetLabel, selectedChar, large = false, onOpenLarge }) => {
  const [data, setData] = useState(glyphCache[family] || null);
  const [error, setError] = useState('');
  const [show, setShow] = useState('all');
  const [query, setQuery] = useState('');
  const [size, setSize] = useState(large ? 60 : 42);
  const [hover, setHover] = useState(null);
  const [altFor, setAltFor] = useState(null); // base glyph whose alternates are shown (▸ marker)
  const [recent, setRecent] = useState(() => readList(RECENT_KEY, 20));
  const [favs, setFavs] = useState(() => readList(FAV_KEY, 200));

  useEffect(() => {
    if (glyphCache[family]) {
      setData(glyphCache[family]);
      return undefined;
    }
    let cancelled = false;
    setData(null);
    setError('');
    axios
      .get(glyphsUrl(family))
      .then(({ data: body }) => {
        if (cancelled) return;
        let d = body;
        try {
          if (body && body.enc === 'b64') d = JSON.parse(atob(body.data)); // see /fonts/charmap (backend)
        } catch (e) {
          d = null;
        }
        if (!d || !Array.isArray(d.glyphs)) {
          // an HTML page instead of the list (a filter or proxy on this computer): say so, don't show "no glyphs"
          setError('The glyph list was blocked on this computer (a filter returned another page). Please try again.');
          return;
        }
        glyphCache[family] = d;
        setData(d);
      })
      .catch((e) => !cancelled && setError(e?.response?.status === 404 ? 'This font has no glyph list.' : 'Could not load the glyphs. Please try again.'));
    return () => {
      cancelled = true;
    };
  }, [family]);

  // a font with decorative alternates opens on them (the reason to open this panel); otherwise the whole font
  useEffect(() => {
    setShow(data?.family === family && data?.alternates ? 'Alternates' : 'all');
    setAltFor(null);
  }, [family, data]);

  const counts = useMemo(() => {
    const c = {};
    (data?.glyphs || []).forEach((g) => {
      c[g.group] = (c[g.group] || 0) + 1;
    });
    return c;
  }, [data]);

  const byCp = useMemo(() => {
    const m = {};
    (data?.glyphs || []).forEach((g) => {
      m[g.cp] = g;
    });
    return m;
  }, [data]);
  /** base + all its alternates (the font's alternate tables, plus the reverse "of" links) */
  const familyOf = (cp) => {
    const base = byCp[cp]?.of ?? cp;
    const set = new Set([base, ...(byCp[base]?.alts || [])]);
    (data?.glyphs || []).forEach((g) => {
      if (g.of === base) set.add(g.cp);
    });
    return [...set].filter((c) => byCp[c]);
  };
  const selCp = selectedChar ? selectedChar.codePointAt(0) : null;
  const selAlts = selCp != null && byCp[selCp] ? familyOf(selCp) : [];
  const altRow = altFor != null ? familyOf(altFor) : [];

  const q = query.trim().toLowerCase();
  const qHex = q.replace(/^u\+/, '');
  const glyphs = useMemo(() => {
    let list = data?.glyphs || [];
    if (show === 'favorites') return favs.filter((f) => f.family === family).map((f) => ({ cp: f.ch.codePointAt(0), name: f.name || '' }));
    if (show !== 'all') list = list.filter((g) => g.group === show);
    if (q) {
      list = list.filter(
        (g) =>
          (g.name || '').toLowerCase().includes(q) ||
          (/^[0-9a-f]{2,6}$/.test(qHex) && g.cp.toString(16).includes(qHex)) ||
          String.fromCodePoint(g.cp) === query.trim()
      );
    }
    return list;
  }, [data, show, q, qHex, query, favs, family]);

  const isFav = (ch, fam) => favs.some((f) => f.ch === ch && f.family === fam);
  const toggleFav = (ch, fam, name) => {
    const next = isFav(ch, fam) ? favs.filter((f) => !(f.ch === ch && f.family === fam)) : [{ ch, family: fam, name }, ...favs];
    setFavs(next);
    writeList(FAV_KEY, next);
  };

  const insert = (ch, fam, name) => {
    onInsert(ch, fam);
    const next = [{ ch, family: fam, name }, ...recent.filter((r) => !(r.ch === ch && r.family === fam))].slice(0, 20);
    setRecent(next);
    writeList(RECENT_KEY, next);
  };

  const cell = (cp, fam, name, key, cellSize = size) => {
    const ch = String.fromCodePoint(cp);
    return (
      <button
        key={key}
        type="button"
        onMouseDown={(e) => e.preventDefault()} // keep the text selection in the box being edited
        onClick={() => insert(ch, fam, name)}
        onMouseEnter={() => setHover({ cp, fam, name })}
        onFocus={() => setHover({ cp, fam, name })}
        onContextMenu={(e) => {
          e.preventDefault();
          toggleFav(ch, fam, name);
        }}
        title={`${name || 'Glyph'} · ${codeOf(cp)} — click to insert, right-click to add to favourites`}
        aria-label={`${name || 'Glyph'} ${codeOf(cp)}`}
        className={cn(
          'relative rounded-md border border-[#EEEBE4] bg-white flex items-center justify-center leading-none text-ink hover:bg-cream hover:border-ink',
          FOCUS_RING
        )}
        style={{ width: cellSize, height: cellSize, fontSize: Math.round(cellSize * 0.58), fontFamily: `'${fam}'` }}
      >
        {ch}
        {isFav(ch, fam) ? <Star className="absolute top-0.5 right-0.5 h-2.5 w-2.5 fill-current text-[#B09536]" aria-hidden="true" /> : null}
        {fam === family && byCp[cp]?.alts?.length ? (
          <span
            role="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              setAltFor(altFor === cp ? null : cp);
            }}
            title="Show alternates"
            className="absolute bottom-0 right-0.5 text-[10px] leading-none text-ink/70 hover:text-ink cursor-pointer"
          >
            ▸
          </span>
        ) : null}
      </button>
    );
  };

  const shownMax = large ? 6000 : 2500;
  const preview = hover || (glyphs[0] ? { cp: glyphs[0].cp, fam: family, name: glyphs[0].name } : null);

  return (
    <div className={cn('flex flex-col gap-3', large && 'min-h-0 h-full')}>
      <div className={cn('flex gap-2', large ? 'flex-row items-center' : 'flex-col')}>
        <div className={large ? 'w-[260px] shrink-0' : ''}>
          <FontPicker value={family} fonts={fonts} onChange={setFamily} />
        </div>
        <label className="flex items-center gap-2 h-10 px-3 rounded-xl border border-[#DCD6C8] bg-white flex-1">
          <Search className="h-4 w-4 text-[#6B665C]" aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search: star, flower, heart, 2766…"
            aria-label="Search glyphs by name or code"
            className="flex-1 bg-transparent outline-none text-sm min-w-0"
          />
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs font-bold text-ink">
          Show
          <select
            value={show}
            onChange={(e) => setShow(e.target.value)}
            className="h-8 rounded-lg border border-[#DCD6C8] bg-white px-2 text-xs font-bold"
            aria-label="Show"
          >
            <option value="all">Entire font ({data?.count ?? '…'})</option>
            <option value="favorites">Favourites ({favs.filter((f) => f.family === family).length})</option>
            {GROUP_ORDER.filter((g) => counts[g]).map((g) => (
              <option key={g} value={g}>
                {g === 'Alternates' ? 'Decorative letters (alternates)' : g} ({counts[g]})
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-0.5 ml-auto" role="group" aria-label="Glyph size">
          {SIZES.map(([px, label]) => (
            <button
              key={px}
              type="button"
              onClick={() => setSize(px)}
              aria-pressed={size === px}
              className={cn('h-7 w-7 rounded-md text-[11px] font-extrabold', size === px ? 'bg-ink text-paper' : 'hover:bg-cream', FOCUS_RING)}
            >
              {label}
            </button>
          ))}
        </div>
        {onOpenLarge ? (
          <button type="button" onClick={onOpenLarge} title="Open the large glyph window" aria-label="Open the large glyph window" className={cn('h-7 w-7 rounded-md flex items-center justify-center hover:bg-cream', FOCUS_RING)}>
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {preview ? (
        <div className="flex items-center gap-3 rounded-xl bg-cream/60 p-2" aria-live="polite">
          <span className="h-[72px] w-[72px] shrink-0 rounded-lg bg-white border border-[#EEEBE4] flex items-center justify-center text-[48px] leading-none text-ink" style={{ fontFamily: `'${preview.fam}'` }}>
            {String.fromCodePoint(preview.cp)}
          </span>
          <span className="min-w-0 text-xs">
            <span className="block font-extrabold text-ink truncate">{preview.name || 'Decorative glyph'}</span>
            <span className="block text-[#6B665C]">{codeOf(preview.cp)} · {preview.fam}</span>
            <button
              type="button"
              onClick={() => toggleFav(String.fromCodePoint(preview.cp), preview.fam, preview.name)}
              className={cn('mt-1 inline-flex items-center gap-1 text-[11px] font-bold underline underline-offset-2', FOCUS_RING)}
            >
              <Star className={cn('h-3 w-3', isFav(String.fromCodePoint(preview.cp), preview.fam) && 'fill-current text-[#B09536]')} aria-hidden="true" />
              {isFav(String.fromCodePoint(preview.cp), preview.fam) ? 'Remove from favourites' : 'Add to favourites'}
            </button>
          </span>
        </div>
      ) : null}

      {selAlts.length > 1 ? (
        <div className="flex flex-col gap-1 rounded-xl border border-pink/40 bg-white p-2" data-testid="selection-alternates">
          <p className={EYEBROW}>Alternates for the selected letter “{selectedChar}”</p>
          <div className="flex flex-wrap gap-1">{selAlts.map((c) => cell(c, family, byCp[c]?.name, `s${c}`, 46))}</div>
          <p className="text-[11px] text-[#6B665C]">Click one to replace the selected letter.</p>
        </div>
      ) : null}
      {altRow.length > 1 ? (
        <div className="flex flex-col gap-1 rounded-xl border border-[#E6E1D6] bg-white p-2">
          <div className="flex items-center justify-between">
            <p className={EYEBROW}>Alternates for “{String.fromCodePoint(byCp[altFor]?.of ?? altFor)}”</p>
            <button type="button" onClick={() => setAltFor(null)} className="text-xs font-bold underline underline-offset-2">
              Close
            </button>
          </div>
          <div className="flex flex-wrap gap-1">{altRow.map((c) => cell(c, family, byCp[c]?.name, `a${c}`, 46))}</div>
        </div>
      ) : null}

      {recent.length ? (
        <div className="flex flex-col gap-1">
          <p className={EYEBROW}>Recently used</p>
          <div className="flex flex-wrap gap-1">{recent.map((r, i) => cell(r.ch.codePointAt(0), r.family, r.name, `r${i}`, 34))}</div>
        </div>
      ) : null}

      {error ? <p className="text-[13px] text-[#B0003A] bg-[#FDF1F4] rounded-xl px-3 py-2">{error}</p> : null}
      {!data && !error ? (
        <p className="flex items-center gap-2 text-sm text-[#6B665C]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading glyphs…
        </p>
      ) : null}
      {data ? (
        <div className={cn('flex flex-wrap gap-1 content-start', large && 'flex-1 min-h-0 overflow-y-auto pr-1')} data-testid="glyph-grid">
          {glyphs.slice(0, shownMax).map((g) => cell(g.cp, family, g.name, g.cp))}
          {!glyphs.length ? <p className="text-sm text-[#6B665C] py-4">{show === 'favorites' ? 'Right-click a glyph (or use the star) to add it here.' : 'No glyph matches.'}</p> : null}
        </div>
      ) : null}
      <p className="text-[11px] text-[#6B665C]">
        Click to {targetLabel ? `type into “${targetLabel}” (same font) or ` : ''}add a new ornament · right-click = favourite
        {glyphs.length > shownMax ? ` · showing ${shownMax} of ${glyphs.length}` : ''}
      </p>
    </div>
  );
};

/** Glyphs panel (InDesign style) with an "Open large" floating window. */
export const GlyphsPanel = ({ fonts, defaultFamily, onInsert, targetLabel, selectedChar, selectedFamily, heading = true }) => {
  const [family, setFamily] = useState(defaultFamily || fonts?.find((f) => f.filename)?.font_family || 'Noto Sans Symbols 2');
  const [large, setLarge] = useState(false);
  // follow the font of the text box being edited
  useEffect(() => {
    if (selectedFamily) setFamily(selectedFamily);
  }, [selectedFamily]);
  const selChar = selectedFamily === family ? selectedChar : null;
  return (
    <div className="flex flex-col gap-3" data-testid="glyphs-panel">
      {heading ? (
        <PanelHeading>
          Glyphs <span className="italic text-logo-gradient">& ornaments</span>
        </PanelHeading>
      ) : null}
      <GlyphBrowser fonts={fonts} family={family} setFamily={setFamily} onInsert={onInsert} targetLabel={targetLabel} selectedChar={selChar} onOpenLarge={() => setLarge(true)} />
      <Dialog open={large} onOpenChange={setLarge} modal={false}>
        <DialogContent
          className="bg-paper rounded-[24px] border border-[#E6E1D6] shadow-[0_30px_60px_-24px_rgba(26,26,26,0.45)] p-5 w-[min(560px,calc(100vw-24px))] max-w-none h-[calc(100vh-96px)] left-auto right-4 top-[80px] translate-x-0 translate-y-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogTitle className="font-serif text-xl font-semibold">Glyphs</DialogTitle>
          <DialogDescription className="text-[#4A4640] -mt-2 text-sm">
            Every character in the font. Click to insert; the window stays open while you work.
          </DialogDescription>
          <div className="flex-1 min-h-0">
            <GlyphBrowser fonts={fonts} family={family} setFamily={setFamily} onInsert={onInsert} targetLabel={targetLabel} selectedChar={selChar} large />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GlyphsPanel;
